<template>
  <v-app dark>
    <v-navigation-drawer app permanent width="90" color="#07111f">
      <v-list nav>
        <v-list-item>
          <v-icon>mdi-telescope</v-icon>
        </v-list-item>

        <v-divider class="my-4" />

        <v-list-item link class="active">
          <v-icon>mdi-crosshairs-gps</v-icon>
        </v-list-item>

        <v-list-item link>
          <v-icon>mdi-star-outline</v-icon>
        </v-list-item>

        <v-list-item link>
          <v-icon>mdi-cog-outline</v-icon>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-app-bar app color="#081524" elevation="1">
      <v-toolbar-title>
        <div>Pointage Télescope</div>
        <div class="caption grey--text">Coordonnées équatoriales RA/Dec</div>
      </v-toolbar-title>

      <v-spacer />

      <v-chip color="green" text-color="white" small>
        ● Connecté
      </v-chip>
    </v-app-bar>

    <v-main>
      <v-container fluid class="pa-4">
        <v-row>
          <v-col cols="12" md="3">
            <v-card class="panel pa-4 mb-4">
              <h3>Coordonnées cibles</h3>

              <v-text-field label="RA (h m s)" v-model="target.ra" />
              <v-text-field label="Dec (° ' '')" v-model="target.dec" />

              <v-btn color="primary" class="mr-2">J2000</v-btn>
              <v-btn dark>Maintenant</v-btn>
            </v-card>

            <v-card class="panel pa-4">
              <h3>Cible rapide</h3>

              <v-text-field label="Rechercher un objet..." v-model="search" />

              <v-list color="transparent">
                <v-list-item
                  v-for="obj in filteredObjects"
                  :key="obj.name"
                  @click="selectObject(obj)"
                >
                  <v-list-item-content>
                    <v-list-item-title>{{ obj.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ obj.ra }} — {{ obj.dec }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="panel pa-4">
              <h3>Vue du ciel équatoriale</h3>

              <div class="sky-map">
                <div class="north">N</div>
                <div class="south">S</div>
                <div class="east">E</div>
                <div class="west">W</div>

                <div class="circle c1"></div>
                <div class="circle c2"></div>
                <div class="axis horizontal"></div>
                <div class="axis vertical"></div>

                <span
                  v-for="star in stars"
                  :key="star.id"
                  class="star"
                  :style="{ left: star.x + '%', top: star.y + '%' }"
                />

                <div class="current"></div>
                <div class="target-marker"></div>
              </div>

              <div class="legend">
                <span><i class="dot green"></i> Position actuelle</span>
                <span><i class="dot yellow"></i> Position cible</span>
              </div>
            </v-card>

            <v-card class="panel pa-4 mt-4">
              <h3>Contrôles de pointage</h3>

              <div class="controls">
                <v-btn icon><v-icon>mdi-arrow-top-left</v-icon></v-btn>
                <v-btn icon color="primary"><v-icon>mdi-arrow-up</v-icon></v-btn>
                <v-btn icon><v-icon>mdi-arrow-top-right</v-icon></v-btn>

                <v-btn icon color="primary"><v-icon>mdi-arrow-left</v-icon></v-btn>
                <v-btn icon><v-icon>mdi-stop</v-icon></v-btn>
                <v-btn icon color="primary"><v-icon>mdi-arrow-right</v-icon></v-btn>

                <v-btn icon><v-icon>mdi-arrow-bottom-left</v-icon></v-btn>
                <v-btn icon color="primary"><v-icon>mdi-arrow-down</v-icon></v-btn>
                <v-btn icon><v-icon>mdi-arrow-bottom-right</v-icon></v-btn>
              </div>

              <v-select
                label="Pas de déplacement"
                :items="steps"
                v-model="step"
              />

              <v-btn color="primary" class="mr-2">
                Aller à la cible
              </v-btn>

              <v-btn dark>
                Centrer
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="3">
            <v-card class="panel pa-4 mb-4">
              <h3>Écart actuel - cible</h3>

              <p>Δ RA <span class="ok">+00 00 12.4s</span></p>
              <p>Δ Dec <span class="ok">-00 00 08.7"</span></p>
              <p>Séparation <strong class="right">16.1 arcsec</strong></p>

              <v-chip color="green" text-color="white" small>
                Dans la tolérance
              </v-chip>
            </v-card>

            <v-card class="panel pa-4 mb-4">
              <h3>Informations actuelles</h3>

              <p>RA : <span class="right">{{ current.ra }}</span></p>
              <p>Dec : <span class="right">{{ current.dec }}</span></p>
              <p>Azimut : <span class="right">181.2°</span></p>
              <p>Hauteur : <span class="right">45.7°</span></p>
              <p>Temps sidéral : <span class="right">12:45:32</span></p>
            </v-card>

            <v-card class="panel pa-4">
              <h3>Statut</h3>

              <p>Monture <span class="ok">Suivi actif</span></p>
              <p>Guidage <span class="ok">Actif</span></p>
              <p>Communication <span class="ok">OK</span></p>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
export default {
  name: 'TelescopePointing',

  data() {
    return {
      search: '',
      step: '0.1°',

      steps: ['0.01°', '0.1°', '1°', '10°'],

      target: {
        ra: '05 35 17.3',
        dec: '-05 23 28'
      },

      current: {
        ra: '05 35 17.4',
        dec: '-05 23 19'
      },

      objects: [
        { name: 'M 31', ra: '00 42 44.3', dec: '+41 16 09' },
        { name: 'M 42', ra: '05 35 17.3', dec: '-05 23 28' },
        { name: 'Vega', ra: '18 36 56.3', dec: '+38 47 01' },
        { name: 'Polaris', ra: '02 31 49.1', dec: '+89 15 51' }
      ],

      stars: []
    }
  },

  computed: {
    filteredObjects() {
      const value = this.search.toLowerCase()

      return this.objects.filter(obj =>
        obj.name.toLowerCase().includes(value)
      )
    }
  },

  created() {
    this.stars = Array.from({ length: 140 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100
    }))
  },

  methods: {
    selectObject(obj) {
      this.target.ra = obj.ra
      this.target.dec = obj.dec
    }
  }
}
</script>

<style scoped>
.panel {
  background: linear-gradient(180deg, #0b1a2d, #07111f) !important;
  border: 1px solid #1d3554;
  border-radius: 12px;
}

.active {
  background: #123d75;
  border-radius: 10px;
}

.sky-map {
  position: relative;
  height: 560px;
  max-width: 560px;
  margin: 20px auto;
  border-radius: 50%;
  background:
    radial-gradient(circle, rgba(60, 120, 180, 0.2), transparent 60%),
    #030814;
  border: 1px solid #39506f;
  overflow: hidden;
}

.circle {
  position: absolute;
  border: 1px solid #2c4868;
  border-radius: 50%;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}

.c1 {
  width: 70%;
  height: 70%;
}

.c2 {
  width: 40%;
  height: 40%;
}

.axis {
  position: absolute;
  background: #2c4868;
}

.horizontal {
  width: 100%;
  height: 1px;
  top: 50%;
}

.vertical {
  height: 100%;
  width: 1px;
  left: 50%;
}

.star {
  position: absolute;
  width: 2px;
  height: 2px;
  background: white;
  border-radius: 50%;
  opacity: 0.8;
}

.current {
  position: absolute;
  left: 49%;
  top: 49%;
  width: 24px;
  height: 24px;
  border: 2px solid #6cff5b;
  border-radius: 50%;
}

.target-marker {
  position: absolute;
  left: 52%;
  top: 51%;
  width: 28px;
  height: 28px;
  border-left: 2px solid #ffd400;
  border-top: 2px solid #ffd400;
}

.north,
.south,
.east,
.west {
  position: absolute;
  color: #cbd6e2;
  font-weight: bold;
}

.north {
  top: 8px;
  left: 50%;
}

.south {
  bottom: 8px;
  left: 50%;
}

.east {
  left: 8px;
  top: 50%;
}

.west {
  right: 8px;
  top: 50%;
}

.controls {
  display: grid;
  grid-template-columns: repeat(3, 56px);
  gap: 8px;
  margin-bottom: 20px;
}

.legend {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.green {
  background: #6cff5b;
}

.yellow {
  background: #ffd400;
}

.ok {
  color: #4dff73;
  float: right;
}

.right {
  float: right;
}
</style>